import { useEffect, useState } from 'react';
import type { QuoteRecord } from '../types';
import { fetchQuoteById } from './api';
import { getQuoteById, saveQuote } from './skyluxStore';

/** Load a quote for public booking/payment pages: server first, then browser cache. */
export function usePublicQuote(quoteId: string | null) {
  const [quote, setQuote] = useState<QuoteRecord | undefined>(undefined);
  const [loading, setLoading] = useState(!!quoteId);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!quoteId) {
      setQuote(undefined);
      setLoading(false);
      setError(null);
      return;
    }

    let active = true;
    setLoading(true);
    setError(null);

    const cached = getQuoteById(quoteId);
    if (cached) setQuote(cached);

    void (async () => {
      try {
        const fromApi = await fetchQuoteById(quoteId);
        if (!active) return;
        setQuote(fromApi);
        saveQuote(fromApi);
        setError(null);
      } catch (e) {
        if (!active) return;
        if (cached) {
          setQuote(cached);
          setError(null);
        } else {
          setQuote(undefined);
          setError(e instanceof Error ? e.message : 'Quote not found');
        }
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [quoteId]);

  return { quote, loading, error };
}
